/**
 * Copyright 2025, IntraLinks, Inc. All rights reserved.
 */
import React, { useEffect, useState } from 'react';
import {
  IlButton,
  IlDataTableV3, IlDropdown, IlRadioButton, IlRadioButtonGroup
} from 'il-framework-component';
import classnames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import EmailPreferencesStyles from './EmailPreferences.scss';
import {
  loadPreferencesData, updateEmailPreferences
} from './EmailPreferencesSlice';
import { RootState } from '../../../app/store';
import { setIsLoading, showToast } from '../../../app/shared/AppSlice/AppSlice';
import {
  DEAL_CENTRE_APP_ID,
  EmailFrequencyList,
  I18nNamespaces
} from '../../../app/shared/utils/constants.util';
import { IEmailPreferences } from '../../../domain/models/IEmailPreferences';
import { TEmailFrequency } from '../../../domain/models/IProfileApps';
import { IDeal } from '../../../infrastructure/repositories/IDealCentreRepository';

export interface IDealLevelPreference {
  name: string;
  id: string;
  deliveryFrequency: TEmailFrequency;
}
const dealFrequencyCellRenderer = (props: any) => (
  <IlRadioButtonGroup
    orientation='horizontal'
    valueSelected={props.value}
    onChange={(value): void => {
      props.setValue(value);
      props.dealLevelPreferences
        .filter((dlp: IDealLevelPreference) => dlp.id === props.data.id)
        .map((dlp: IDealLevelPreference) => ({ ...dlp, deliveryFrequency: value }));
      props.setDealLevelPreferences([...props.dealLevelPreferences]);
    }}
  >
    {EmailFrequencyList.map((item) => (
      <IlRadioButton
        labelPosition='right'
        labelText={props.t(`emailPreferences.${item}`)}
        value={item}
      />
    ))}
  </IlRadioButtonGroup>
);

const ALL_VS_INDIVIDUAL_DEAL = ['ALL', 'INDIVIDUAL_DEAL'];

// dealCenterPreferencesMap.length === 0 means that the user has not set any preferences for the Deal Centre.
// so we set the preferences for all deals to true.
function arePreferencesSetForAllDeals(dealCenterPreferencesMap: Map<string, TEmailFrequency>) {
  return dealCenterPreferencesMap.size === 0 || dealCenterPreferencesMap.has(DEAL_CENTRE_APP_ID);
}

function getDeliveryFrequencyForAllDeals(
  dealCenterPreferencesMap: Map<string, TEmailFrequency>,
  defaultPreferencesMap: Map<string, TEmailFrequency>
) {
  return dealCenterPreferencesMap.get(DEAL_CENTRE_APP_ID) || defaultPreferencesMap.get(DEAL_CENTRE_APP_ID);
}

function getDealLevelDeliveryFrequency(
  deals: IDeal[],
  dealCenterPreferencesMap: Map<string, TEmailFrequency>,
  defaultPreferencesMap: Map<string, TEmailFrequency>
) {
  return deals.map((deal: { name: string; id: string }) => ({
    name: deal.name,
    id: deal.id,
    deliveryFrequency:
      dealCenterPreferencesMap.get(deal.id)
      || dealCenterPreferencesMap.get(DEAL_CENTRE_APP_ID)
      || defaultPreferencesMap.get(DEAL_CENTRE_APP_ID)!
  }));
}

const DealCentreEmailPreferences: React.FC = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation(I18nNamespaces.PROFILE);
  const [isEditable, setIsEditable] = useState(false);
  const [deliveryFrequencySetForAllDeals, setDeliveryFrequencySetForAllDeals] = useState(true);
  const {
    fetchDealsFailed,
    fetchDefaultPreferencesFailed,
    fetchDealCenterPreferencesFailed,
    updateRequestFailed,
    updateRequestSuccess,
    fetchCompleted,
    updateCompleted,
    defaultPreferencesMap,
    dealCenterPreferencesMap,
    deals
  } = useSelector((state: RootState) => state.emailPreferencesSettings);
  const [deliveryFrequencyForAllDeals, setDeliveryFrequencyForAllDeals] = useState<TEmailFrequency>();
  const [dealLevelPreferences, setDealLevelPreferences] = useState<IDealLevelPreference[]>([]);

  const initializePreferences = () => {
    setIsEditable(false);
    setDeliveryFrequencySetForAllDeals(arePreferencesSetForAllDeals(dealCenterPreferencesMap));
    setDeliveryFrequencyForAllDeals(
      getDeliveryFrequencyForAllDeals(dealCenterPreferencesMap, defaultPreferencesMap)
    );
    setDealLevelPreferences(
      getDealLevelDeliveryFrequency(deals, dealCenterPreferencesMap, defaultPreferencesMap)
    );
    dispatch(setIsLoading(false));
  };

  useEffect(() => {
    initializePreferences();
  }, [dealCenterPreferencesMap, deals]);

  useEffect(() => {
    dispatch(setIsLoading(true));
    dispatch(loadPreferencesData(DEAL_CENTRE_APP_ID));
  }, []);

  useEffect(() => {
    if (updateCompleted) {
      if (updateRequestSuccess) {
        (async (): Promise<void> => {
          dispatch(loadPreferencesData(DEAL_CENTRE_APP_ID));
        })();
        dispatch(showToast({
          title: t('emailPreferences.updateEmailPreferences'),
          timeout: 0 as number,
          kind: 'success'
        }));
      } else if (updateRequestFailed) {
        dispatch(setIsLoading(false));
        dispatch(showToast({
          title: t('emailPreferences.errorMessages.updateEmailPreferences'),
          timeout: 0 as number,
          kind: 'error'
        }));
      }
    }
  }, [updateCompleted]);

  useEffect(() => {
    if (fetchCompleted) {
      if (fetchDealsFailed) {
        dispatch(showToast({
          title: t('emailPreferences.errorMessages.fetchDeals'),
          timeout: 0 as number,
          kind: 'error'
        }));
      }
      if (fetchDealCenterPreferencesFailed) {
        dispatch(showToast({
          title: t('emailPreferences.errorMessages.fetchDealCenterPreferences'),
          timeout: 0 as number,
          kind: 'error'
        }));
      }
      if (fetchDefaultPreferencesFailed) {
        dispatch(showToast({
          title: t('emailPreferences.errorMessages.fetchDefaultPreferences'),
          timeout: 0 as number,
          kind: 'error'
        }));
      }
    }
  }, [fetchCompleted]);

  const handleSubmit = () => {
    dispatch(setIsLoading(true));
    let updateReq: IEmailPreferences[];
    if (deliveryFrequencySetForAllDeals) {
      updateReq = [{
        containerId: DEAL_CENTRE_APP_ID,
        preference: { frequency: deliveryFrequencyForAllDeals! },
        containerType: 'DEAL'
      }];
    } else {
      updateReq = dealLevelPreferences.map((deal) => ({
        containerId: deal.id,
        preference: { frequency: deal.deliveryFrequency },
        containerType: 'DEAL'
      }));
    }
    dispatch(updateEmailPreferences(DEAL_CENTRE_APP_ID, updateReq));
  };
  return (
    <div className={EmailPreferencesStyles.dealCentreEmailPreferences}>
      {fetchCompleted && fetchDealCenterPreferencesFailed ? (
        <p>
          {t('defaultErrorMessage')}
        </p>
      ) : (
        <div>
          <div>
            {isEditable && (
              <div className={EmailPreferencesStyles.emailPreferencesSubmitButtons}>
                <IlButton
                  kind='secondary'
                  onClick={() => {
                    setIsEditable(false);
                    initializePreferences();
                  }}
                >
                  <span>{t('emailPreferences.cancel')}</span>
                </IlButton>
                <IlButton onClick={handleSubmit}>
                  <span>{t('emailPreferences.save')}</span>
                </IlButton>
              </div>
            )}
            {!isEditable && (
              <div className={EmailPreferencesStyles.emailPreferencesSubmitButtons}>
                <IlButton
                  onClick={() => {
                    setIsEditable(true);
                  }}
                >
                  <span>{t('emailPreferences.edit')}</span>
                </IlButton>
              </div>
            )}
          </div>
          <div>
            <IlRadioButtonGroup
              orientation='horizontal'
              name='Deal Centre Email Preferences'
              valueSelected={deliveryFrequencySetForAllDeals ? ALL_VS_INDIVIDUAL_DEAL[0] : ALL_VS_INDIVIDUAL_DEAL[1]}
              onChange={(value): void => {
                setDeliveryFrequencySetForAllDeals(String(value) === ALL_VS_INDIVIDUAL_DEAL[0]);
              }}
            >
              <IlRadioButton
                disabled={!deliveryFrequencySetForAllDeals && !isEditable}
                labelPosition='right'
                labelText={t('emailPreferences.setForAllDeals')}
                value={ALL_VS_INDIVIDUAL_DEAL[0]}
              />
              <IlRadioButton
                disabled={deals.length === 0 || (deliveryFrequencySetForAllDeals && !isEditable)}
                labelPosition='right'
                labelText={t('emailPreferences.setForIndividualDeals')}
                value={ALL_VS_INDIVIDUAL_DEAL[1]}
              />
            </IlRadioButtonGroup>

            {deliveryFrequencySetForAllDeals && (
              <div className={EmailPreferencesStyles.preferencesSetForAllDeals}>
                <p className={classnames(EmailPreferencesStyles.radioButtonLabel)}>
                  {t('emailPreferences.deliveryFrequency')}:
                </p>
                {!isEditable && (
                  <p className='bx--list-box__label'>{t(`emailPreferences.${deliveryFrequencyForAllDeals}`)}</p>
                )}
                {isEditable && (
                  <IlDropdown
                    id='deal-centre-email-preferences-frequency-dropdown'
                    label={t('emailPreferences.deliveryFrequency')}
                    className={EmailPreferencesStyles.emailPreferencesDropdown}
                    initialSelectedItem={deliveryFrequencyForAllDeals}
                    selectedItem={deliveryFrequencyForAllDeals}
                    itemToString={(item: TEmailFrequency): string => t(`emailPreferences.${item}`)}
                    items={[...EmailFrequencyList]}
                    onChange={(f): void => { setDeliveryFrequencyForAllDeals(f.selectedItem); }}
                  />
                )}
              </div>
            )}
            {!deliveryFrequencySetForAllDeals && (
              <IlDataTableV3
                dynamicHeight
                id='deal-centre-email-preferences-data-table'
                data={dealLevelPreferences}
                columns={[
                  {
                    field: 'name',
                    filter: true,
                    tooltipField: 'name',
                    width: 300
                  },
                  {
                    cellRenderer: (props: any) => (
                      isEditable
                        ? dealFrequencyCellRenderer({
                          ...props, t, dealLevelPreferences, setDealLevelPreferences
                        })
                        : t(`emailPreferences.${props.value}`)
                    ),
                    field: 'deliveryFrequency',
                    width: 400
                  }
                ]}
                selectable={false}
                resizable
              />
            )}
          </div>
        </div>

      )}
    </div>
  );
};

class DealCentreEmailPreferencesClassComponent extends React.Component {
  render(): JSX.Element {
    return (<DealCentreEmailPreferences />);
  }
}
export default DealCentreEmailPreferencesClassComponent;
